import { createColumnsLayout } from 'src/helpers/columnsLayoutUtils';
import { createEmptyPageBlock, newPageBlockId } from 'src/helpers/pageBlockFactory';
import {
  applyVisibilityPreset,
  getBlocksFromPageContent,
} from 'src/helpers/pageContentUtils';
import {
  forEachPageBlock,
  getBodyBlocks,
  IPageDocument,
  normalizePageDocument,
} from 'src/helpers/pageDocumentUtils';
import { LanguageKeys } from 'src/common/constants/languages';
import {
  IColumnsBlock,
  IPageBlock,
  ISignupFlowBlock,
  ITextBlock,
  TextPresentation,
} from 'src/interfaces/PageBlock';
import { IBasePageContent } from 'src/interfaces/PageContent';
import { VisibilityPresetId } from 'src/helpers/pageContentBlockPresets';

type SignupLegacyKey =
  | 'main'
  | 'createAccountDescription'
  | 'temporarySignupDescription'
  | 'manageParticipantsDescription'
  | 'signupClosed'
  | 'successMessage';

const SIGNUP_EMBEDDED_LEGACY_KEYS: SignupLegacyKey[] = [
  'createAccountDescription',
  'temporarySignupDescription',
  'manageParticipantsDescription',
];

/** i18n keys for signup text block labels in the page editor (admin UI). */
const SIGNUP_LEGACY_ADMIN_LABEL_KEYS: Partial<Record<SignupLegacyKey, string>> = {
  main: 'race.signUp.mainContent',
  createAccountDescription: 'race.signUp.createAccountDescriptionLabel',
  temporarySignupDescription: 'race.signUp.temporarySignupDescriptionLabel',
  manageParticipantsDescription: 'race.signUp.manageParticipantsDescriptionLabel',
  signupClosed: 'race.signUp.signupClosedLabel',
  successMessage: 'race.signUp.successMessage',
};

export function signupLegacyAdminLabelKey(
  legacyKey: string | undefined,
): string | undefined {
  if (!legacyKey) {
    return undefined;
  }
  return SIGNUP_LEGACY_ADMIN_LABEL_KEYS[legacyKey as SignupLegacyKey];
}

const SIGNUP_PROTECTED_LEGACY_KEYS: SignupLegacyKey[] = [
  'main',
  'successMessage',
  'signupClosed',
  ...SIGNUP_EMBEDDED_LEGACY_KEYS,
];

const PRESENTATION_BY_LEGACY_KEY: Partial<Record<SignupLegacyKey, TextPresentation>> = {
  main: 'inline',
  createAccountDescription: 'embedded',
  temporarySignupDescription: 'embedded',
  manageParticipantsDescription: 'embedded',
  signupClosed: 'inline',
  successMessage: 'modal',
};

const VISIBILITY_PRESET_BY_LEGACY_KEY: Partial<Record<SignupLegacyKey, VisibilityPresetId>> = {
  createAccountDescription: 'signupOpenGuest',
  temporarySignupDescription: 'signupOpenGuest',
  manageParticipantsDescription: 'signupOpenLoggedIn',
  signupClosed: 'signupClosed',
};

export const SIGNUP_CLOSED_DEFAULT_COPY: Record<LanguageKeys, string> = {
  en: '<p>Registration closed. We look forward to seeing you next time.</p>',
  hu: '<p>Feliratkozás lezárva. Várunk legközelebb (is).</p>',
  ro: '<p>Înscrierile s-au închis. Așteptăm să ne (re)vedem data viitoare.</p>',
};

export const SIGNUP_CREATE_ACCOUNT_DEFAULT_COPY: Record<LanguageKeys, string> = {
  en: '<p>Create a free account to save participants and manage entries across editions.</p>',
  hu: '<p>Hozz létre ingyenes fiókot a résztvevők mentéséhez és a nevezések kezeléséhez a későbbi eseményeken is.</p>',
  ro: '<p>Creează un cont gratuit pentru a salva participanții și a gestiona înscrierile la edițiile viitoare.</p>',
};

export const SIGNUP_TEMPORARY_SIGNUP_DEFAULT_COPY: Record<LanguageKeys, string> = {
  en: '<p>Register once without an account. Your entry applies only to this edition.</p>',
  hu: '<p>Nevezz egyszer fiók nélkül. A nevezés csak erre az eseményre érvényes.</p>',
  ro: '<p>Înscrie-te o singură dată fără cont. Înscrierea este valabilă doar pentru această ediție.</p>',
};

export const SIGNUP_MANAGE_PARTICIPANTS_DEFAULT_COPY: Record<LanguageKeys, string> = {
  en: '<p>Add or update participants in your account, then register them for this edition.</p>',
  hu: '<p>Adj hozzá vagy szerkeszd a fiókodhoz tartozó résztvevőket, majd nevezd őket erre az eseményre.</p>',
  ro: '<p>Adaugă sau actualizează participanții din contul tău, apoi înscrie-i la această ediție.</p>',
};

const SIGNUP_DEFAULT_COPY_BY_LEGACY_KEY: Partial<
  Record<SignupLegacyKey, Record<LanguageKeys, string>>
> = {
  createAccountDescription: SIGNUP_CREATE_ACCOUNT_DEFAULT_COPY,
  temporarySignupDescription: SIGNUP_TEMPORARY_SIGNUP_DEFAULT_COPY,
  manageParticipantsDescription: SIGNUP_MANAGE_PARTICIPANTS_DEFAULT_COPY,
  signupClosed: SIGNUP_CLOSED_DEFAULT_COPY,
};

const LANGUAGE_KEYS: LanguageKeys[] = [LanguageKeys.en, LanguageKeys.hu, LanguageKeys.ro];

/** Fills empty language fields from legacy sections, then canonical defaults. */
function resolveSignupBlockI18n(
  legacyKey: SignupLegacyKey,
  sources: Partial<Record<LanguageKeys, string>> = {},
): Record<LanguageKeys, string> {
  const defaults = SIGNUP_DEFAULT_COPY_BY_LEGACY_KEY[legacyKey];
  if (!defaults) {
    return {
      en: sources.en ?? '',
      hu: sources.hu ?? '',
      ro: sources.ro ?? '',
    };
  }
  return LANGUAGE_KEYS.reduce(
    (acc, lang) => {
      acc[lang] = (sources[lang] ?? '').trim() || defaults[lang];
      return acc;
    },
    { en: '', hu: '', ro: '' } as Record<LanguageKeys, string>,
  );
}

export function signupTextPresentationForLegacyKey(
  legacyKey: string | undefined,
): TextPresentation {
  if (legacyKey && legacyKey in PRESENTATION_BY_LEGACY_KEY) {
    return PRESENTATION_BY_LEGACY_KEY[legacyKey as SignupLegacyKey]!;
  }
  return 'inline';
}

export function documentHasSignupFlowBlock(doc: IPageDocument): boolean {
  let found = false;
  forEachPageBlock(doc, block => {
    if (block.type === 'signupFlow') found = true;
  });
  return found;
}

export function isSignupProtectedBlock(block: IPageBlock): boolean {
  if (block.type === 'signupFlow') return true;
  if (block.type === 'text' && block.legacyKey) {
    return (SIGNUP_PROTECTED_LEGACY_KEYS as readonly string[]).includes(block.legacyKey);
  }
  return false;
}

export function shouldRenderTextBlockInline(block: ITextBlock, _isEditMode: boolean): boolean {
  const presentation = block.textPresentation ?? signupTextPresentationForLegacyKey(block.legacyKey);
  return presentation === 'inline';
}

export function findSignupTextBlockByLegacyKey(
  pageContent: IBasePageContent | undefined,
  legacyKey: SignupLegacyKey,
): ITextBlock | undefined {
  const blocks = getBlocksFromPageContent(pageContent);
  return blocks.find(
    (b): b is ITextBlock => b.type === 'text' && b.legacyKey === legacyKey,
  );
}

export function findSignupTextBlockByLegacyKeyInDocument(
  doc: IPageDocument,
  legacyKey: SignupLegacyKey,
): ITextBlock | undefined {
  let found: ITextBlock | undefined;
  forEachPageBlock(doc, block => {
    if (block.type === 'text' && block.legacyKey === legacyKey) {
      found = block as ITextBlock;
    }
  });
  return found;
}

function createSignupTextBlock(
  legacyKey: SignupLegacyKey,
  i18n: Partial<Record<LanguageKeys, string>> = {},
): ITextBlock {
  const block: ITextBlock = {
    id: newPageBlockId(),
    type: 'text',
    sortOrder: 0,
    legacyKey,
    textPresentation: signupTextPresentationForLegacyKey(legacyKey),
    i18n: resolveSignupBlockI18n(legacyKey, i18n),
  };
  const preset = VISIBILITY_PRESET_BY_LEGACY_KEY[legacyKey];
  if (preset) {
    block.visibility = applyVisibilityPreset(preset);
  }
  return block;
}

function isSignupMainBlock(block: IPageBlock): block is ITextBlock {
  return block.type === 'text' && block.legacyKey === 'main';
}

function isSignupFlowBlock(block: IPageBlock): block is ISignupFlowBlock {
  return block.type === 'signupFlow';
}

function isCompetitionsListBlock(block: IPageBlock): boolean {
  return block.type === 'competitionsList';
}

function isSignupColumnContentBlock(block: IPageBlock): boolean {
  return isSignupMainBlock(block) || isSignupFlowBlock(block) || isCompetitionsListBlock(block);
}

function isSignupLayoutColumnsBlock(block: IPageBlock): block is IColumnsBlock {
  if (block.type !== 'columns') return false;
  const nested = (block.columns ?? []).flatMap(col => col.blocks ?? []);
  return nested.some(isSignupFlowBlock);
}

function isSignupColumnsBlock(block: IPageBlock): block is IColumnsBlock {
  return isSignupLayoutColumnsBlock(block);
}

function findSignupColumnsBlock(blocks: IPageBlock[]): IColumnsBlock | undefined {
  const candidates = getBodyBlocks({ blocks }).filter(isSignupLayoutColumnsBlock);
  if (!candidates.length) return undefined;
  return (
    candidates.find(b => b.desktopColumnLayout === '2-66-33') ??
    candidates[0]
  );
}

function dedupeColumnBlocks(blocks: IPageBlock[]): IPageBlock[] {
  const result: IPageBlock[] = [];
  let hasMain = false;
  let hasFlow = false;
  let hasList = false;

  for (const block of blocks) {
    if (isSignupMainBlock(block)) {
      if (hasMain) continue;
      hasMain = true;
    } else if (isSignupFlowBlock(block)) {
      if (hasFlow) continue;
      hasFlow = true;
    } else if (isCompetitionsListBlock(block)) {
      if (hasList) continue;
      hasList = true;
    }
    result.push(block);
  }

  return result;
}

function findCompetitionsListBlock(doc: IPageDocument): IPageBlock | undefined {
  return allBlocksFlat(doc).find(isCompetitionsListBlock);
}

function createOrReuseCompetitionsListBlock(doc: IPageDocument): IPageBlock {
  const existing = findCompetitionsListBlock(doc);
  if (existing) return existing;
  const block = createEmptyPageBlock('competitionsList', 1);
  block.visibility = applyVisibilityPreset('signupOpenWithEdition');
  return block;
}

function ensureSignupColumnsContent(
  columnsBlock: IColumnsBlock,
  mainBlock: ITextBlock,
  flowBlock: ISignupFlowBlock,
  competitionsListBlock: IPageBlock,
): void {
  const columns = columnsBlock.columns ?? [];
  if (columns.length < 2) return;

  const col0 = { ...columns[0]!, blocks: [...(columns[0]?.blocks ?? [])] };
  const col1 = { ...columns[1]!, blocks: [...(columns[1]?.blocks ?? [])] };

  if (!col0.blocks.some(isSignupMainBlock)) {
    col0.blocks.unshift(mainBlock);
  }
  if (!col0.blocks.some(isCompetitionsListBlock)) {
    if (!col0.blocks.some(b => b.id === competitionsListBlock.id)) {
      col0.blocks.push(competitionsListBlock);
    }
  }
  if (!col1.blocks.some(isSignupFlowBlock)) {
    if (!col1.blocks.some(b => b.id === flowBlock.id)) {
      col1.blocks.unshift(flowBlock);
    }
  }

  col0.blocks = dedupeColumnBlocks(col0.blocks);
  col1.blocks = dedupeColumnBlocks(col1.blocks);
  columnsBlock.columns = [col0, col1, ...columns.slice(2)];
  columnsBlock.desktopColumnLayout = '2-66-33';
  columnsBlock.columnsPerRow = { xs: 1, sm: 1, md: 2 };
}

function dedupeSignupBodyBlocks(
  bodyBlocks: IPageBlock[],
  columnsBlock: IColumnsBlock,
): IPageBlock[] {
  return bodyBlocks.filter(block => {
    if (isSignupColumnsBlock(block)) {
      return block.id === columnsBlock.id;
    }
    return !isSignupColumnContentBlock(block);
  });
}

function createSignupColumnsBlock(
  mainBlock: ITextBlock,
  flowBlock: IPageBlock,
  competitionsList: IPageBlock,
): IColumnsBlock {
  const layout = createColumnsLayout(2);

  return {
    id: newPageBlockId(),
    type: 'columns',
    sortOrder: 0,
    zone: 'body',
    ...layout,
    desktopColumnLayout: '2-66-33',
    columnsPerRow: { xs: 1, sm: 1, md: 2 },
    columns: [
      {
        ...layout.columns[0]!,
        alignH: 'left',
        blocks: [mainBlock, competitionsList],
      },
      {
        ...layout.columns[1]!,
        alignH: 'left',
        blocks: [flowBlock],
      },
    ],
  };
}

function allBlocksFlat(doc: IPageDocument): IPageBlock[] {
  const flat: IPageBlock[] = [];
  forEachPageBlock(doc, block => flat.push(block));
  return flat;
}

function hasLegacyKey(doc: IPageDocument, legacyKey: SignupLegacyKey): boolean {
  return allBlocksFlat(doc).some(
    b => b.type === 'text' && (b as ITextBlock).legacyKey === legacyKey,
  );
}

function findMainBlock(doc: IPageDocument): ITextBlock | undefined {
  return allBlocksFlat(doc).find(
    (b): b is ITextBlock => b.type === 'text' && b.legacyKey === 'main',
  );
}

/** Merges missing canonical signup blocks into the document without duplicating existing ones. */
export function mergeRecommendedSignupLayout(
  doc: IPageDocument,
  pageContent?: IBasePageContent,
): IPageDocument {
  const next: IPageDocument = {
    ...doc,
    blocks: doc.blocks.map(b => ({ ...b })),
  };

  const legacySections = pageContent?.content ?? [];
  const sectionI18n = (name: string) =>
    legacySections.find(s => s.name === name)?.i18n;

  for (const legacyKey of SIGNUP_EMBEDDED_LEGACY_KEYS) {
    if (!hasLegacyKey(next, legacyKey)) {
      next.blocks.push(
        createSignupTextBlock(legacyKey, sectionI18n(legacyKey)),
      );
    }
  }

  if (!hasLegacyKey(next, 'signupClosed')) {
    next.blocks.push(createSignupTextBlock('signupClosed', SIGNUP_CLOSED_DEFAULT_COPY));
  }

  if (!hasLegacyKey(next, 'successMessage')) {
    next.blocks.push(
      createSignupTextBlock('successMessage', sectionI18n('successMessage')),
    );
  }

  let mainBlock = findMainBlock(next);
  if (!mainBlock) {
    mainBlock = createSignupTextBlock('main', sectionI18n('main'));
    next.blocks.unshift(mainBlock);
  } else {
    mainBlock.textPresentation = 'inline';
  }

  let flowBlock: ISignupFlowBlock = allBlocksFlat(next).find(
    (b): b is ISignupFlowBlock => b.type === 'signupFlow',
  ) ?? (() => {
    const created = createEmptyPageBlock('signupFlow', 0) as ISignupFlowBlock;
    created.visibility = applyVisibilityPreset('signupOpenWithEdition');
    next.blocks.push(created);
    return created;
  })();

  const competitionsListBlock = createOrReuseCompetitionsListBlock(next);
  const heroBlocks = next.blocks.filter(b => b.zone === 'hero');
  let bodyBlocks = getBodyBlocks(next);

  let columnsBlock = findSignupColumnsBlock(bodyBlocks);
  if (!columnsBlock) {
    columnsBlock = createSignupColumnsBlock(mainBlock, flowBlock, competitionsListBlock);
    bodyBlocks = dedupeSignupBodyBlocks(bodyBlocks, columnsBlock);
    next.blocks = [...heroBlocks, columnsBlock, ...bodyBlocks];
  } else {
    ensureSignupColumnsContent(columnsBlock, mainBlock, flowBlock, competitionsListBlock);
    bodyBlocks = dedupeSignupBodyBlocks(bodyBlocks, columnsBlock);
    next.blocks = [...heroBlocks, ...bodyBlocks];
  }

  forEachPageBlock(next, block => {
    if (block.type !== 'text' || !block.legacyKey) return;
    const text = block as ITextBlock;
    const legacyKey = text.legacyKey as SignupLegacyKey;
    if (legacyKey in SIGNUP_DEFAULT_COPY_BY_LEGACY_KEY) {
      text.i18n = resolveSignupBlockI18n(legacyKey, {
        ...sectionI18n(legacyKey),
        ...text.i18n,
      });
    }
    text.textPresentation = signupTextPresentationForLegacyKey(legacyKey);
    const preset = VISIBILITY_PRESET_BY_LEGACY_KEY[legacyKey];
    if (preset && !text.visibility?.rules?.length) {
      text.visibility = applyVisibilityPreset(preset);
    }
  });

  return normalizePageDocument({ blocks: next.blocks });
}
